import type { Location } from "history";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { ChipType } from "../components/Chips/Chips";
import { loadFilters } from "./filterStateManager";
import {
  buildFilterKeyToColumnIdMap,
  getListFilterCarryEntryKey,
  isListFilterCarryDismissed,
  markListFilterCarryDismissed,
  resolveColumnIdForFilterKey,
} from "./listFilterCarryOver";
import type { CarryOverColumn } from "./listFilterCarryOver";

const SKIP_KEYS = new Set([
  "q",
  "offset",
  "perPage",
  "limit",
  "tab",
  "_fs",
  "active_tag_view",
  "status",
  "template",
  "show_inactive",
]);

export type UseListFilterCarryOverArgs = {
  /** Domain discriminator, e.g. `"product"` or an MDM entity type. */
  domain: string;
  /** Product or entity id. */
  scopeId: string;
  collectionId: string;
  /** sessionStorage key holding the list page's `location.search`. */
  listSearchStorageKey: string;
  columns: CarryOverColumn[] | undefined;
  translateDisplayName?: (displayName: string) => string;
  setActiveFilters: Dispatch<SetStateAction<Record<string, ChipType[]>>>;
};

/**
 * Carry list-page filters into a collection table's `activeFilters`, and only
 * mark the list-search snapshot dismissed when the user clears filters that
 * originated from that carry-over.
 */
export function useListFilterCarryOver({
  domain,
  scopeId,
  collectionId,
  listSearchStorageKey,
  columns,
  translateDisplayName,
  setActiveFilters,
}: UseListFilterCarryOverArgs): {
  markDismissedIfCarriedOver: () => void;
} {
  const didCarryOverRef = useRef(false);
  const appliedEntryKeyRef = useRef<string | null>(null);
  const carryKeyRef = useRef<string | null>(null);

  const filterKeyToId = useMemo(
    () =>
      columns?.length
        ? buildFilterKeyToColumnIdMap(columns, translateDisplayName)
        : {},
    [columns, translateDisplayName]
  );

  useEffect(() => {
    if (!columns?.length) return;

    const listSearch = sessionStorage.getItem(listSearchStorageKey);
    if (!listSearch) return;

    const entryKey = getListFilterCarryEntryKey(
      domain,
      scopeId,
      collectionId,
      listSearch
    );
    // Same mount cycle / dependency re-run.
    if (carryKeyRef.current === entryKey) return;

    // User cleared carried-over filters for this list-search snapshot.
    if (isListFilterCarryDismissed(entryKey)) return;

    carryKeyRef.current = entryKey;

    const listFilters = loadFilters({
      search: listSearch.startsWith("?") ? listSearch : `?${listSearch}`,
      pathname: "",
      state: undefined,
      hash: "",
    } as Location);

    setActiveFilters((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(listFilters).forEach(([key, values]) => {
        if (SKIP_KEYS.has(key) || !values?.length) return;
        const colId = resolveColumnIdForFilterKey(filterKeyToId, key);
        if (!colId || (next[colId]?.length ?? 0) > 0) return;
        // AP-1016 (Advanced Filters, Part 1): a numeric filter's `values`
        // array is a single opaque encoded criterion string (see
        // encodeNumericFilterCriterion) — it carries over unchanged here,
        // same as any chip value, since `matchesFilter`'s numeric branch and
        // NumericFilterInput's prefill both key off the destination column's
        // `input_type` and read only `chip.name`, not `chip.type`.
        next[colId] = values.map((v) => ({ name: v, id: v }));
        changed = true;
      });
      if (changed) {
        didCarryOverRef.current = true;
        appliedEntryKeyRef.current = entryKey;
      }
      return changed ? next : prev;
    });
  }, [
    domain,
    scopeId,
    collectionId,
    listSearchStorageKey,
    columns,
    filterKeyToId,
    setActiveFilters,
  ]);

  const markDismissedIfCarriedOver = useCallback(() => {
    if (!didCarryOverRef.current) return;

    const listSearch = sessionStorage.getItem(listSearchStorageKey) ?? "";
    const entryKey =
      appliedEntryKeyRef.current ??
      (listSearch
        ? getListFilterCarryEntryKey(domain, scopeId, collectionId, listSearch)
        : null);
    if (!entryKey) return;

    markListFilterCarryDismissed(entryKey);
    didCarryOverRef.current = false;
  }, [domain, scopeId, collectionId, listSearchStorageKey]);

  return { markDismissedIfCarriedOver };
}
